// ---------- Utilities ----------
export function indexToName ( i ) {
	// 0 => "a", 1 => "b", ... 25 => "z", 26 => "aa", ...
	let n = i + 1
	let s = ''
	while ( n > 0 ) {
		n--
		s = String.fromCharCode( 97 + ( n % 26 ) ) + s
		n = Math.floor( n / 26 )
	}
	return s
}

/**
 * Build a numeric grid (rows × cols) and fill with indices [start .. start+count-1]
 * - If count < rows*cols, last row will be stretched instead of padded with -1.
 * - Returns: { grid: number[][], rows, cols, count } where empty cell = -1.
 */
export function makeIndexGrid ( count, rows, cols, startIndex = 0 ) {
	const grid = Array.from( { length: rows }, () => Array( cols ).fill( -1 ) )
	let idx = startIndex
	for ( let r = 0; r < rows; r++ ) {
		for ( let c = 0; c < cols; c++ ) {
			if ( idx < startIndex + count ) {
				grid[r][c] = idx
				idx++
			}
		}
	}

	// stretch the last row if partially filled
	const lastRow = grid[rows - 1]
	const filled = lastRow.filter( ( v ) => v !== -1 )
	if ( filled.length > 0 && filled.length < cols ) {
		const stretchRow = []
		const stretchFactor = Math.floor( cols / filled.length )
		const extras = cols % filled.length
		let k = 0
		for ( let cell of filled ) {
			const repeatCount = stretchFactor + ( k < extras ? 1 : 0 )
			for ( let i = 0; i < repeatCount; i++ ) stretchRow.push( cell )
			k++
		}
		grid[rows - 1] = stretchRow
	}

	return { grid, rows, cols, count }
}

/**
 * Choose rows/cols for a given count and maxCols.
 * Rules:
 *  - Always prefer taller (rows ≥ cols).
 *  - If count > maxCols*3 => fix cols = maxCols (tall layout).
 */
function chooseGridShape ( count, maxCols ) {
	if ( count <= 0 ) return { rows: 0, cols: 0 }

	if ( count > maxCols * 3 ) {
		const cols = maxCols
		const rows = Math.ceil( count / cols )
		return { rows, cols }
	}

	let best = { rows: Infinity, cols: 1, cost: Infinity }
	for ( let c = 1; c <= maxCols; c++ ) {
		const r = Math.ceil( count / c )
		// prefer more rows than cols
		if ( r < c ) continue
		const slots = r * c
		const leftover = slots - count
		const lonelyPenalty = leftover === 1 ? 1000 : 0
		const cost = Math.abs( r - c ) + leftover + lonelyPenalty
		if ( cost < best.cost ) {
			best = { rows: r, cols: c, cost }
		}
	}
	return { rows: best.rows, cols: best.cols }
}

// ---------- Factor-based grid builder ----------
function generateFactorIndexGrid ( count, maxCols ) {
	if ( count <= 0 ) return { grid: [], rows: 0, cols: 0 }
	const { rows, cols } = chooseGridShape( count, maxCols )
	return makeIndexGrid( count, rows, cols, 0 )
}

// ---------- Helper to convert numeric grid to CSS ----------
function buildGridResponse ( grid, names ) {
	const rows = grid.length
	const cols = rows > 0 ? grid[0].length : 0
	const lines = grid.map( ( row ) => `"${row.map( ( idx ) => ( idx === -1 ? '.' : indexToName( idx ) ) ).join( ' ' )}"` )
	return {
		areas: lines.join( '\n' ),
		names,
		rows,
		cols,
		style: {
			display: 'grid',
			gridTemplateAreas: lines.join( '\n' ),
			gridTemplateColumns: `repeat(${cols}, 1fr)`,
			gridTemplateRows: `repeat(${rows}, auto)`,
			gap: '8px',
		},
	}
}

// ---------- Main unified function ----------
export function generateMeetingGrid (
	n,
	opts
) {
	if ( !Number.isFinite( n ) || n <= 0 ) {
		return { areas: '', names: [], rows: 0, cols: 0, style: {} }
	}

	const width = opts?.width ?? 1200
	const minCardWidth = opts?.minCardWidth ?? 200
	const computedCols = Math.max( 1, Math.floor( width / minCardWidth ) )
	const maxCols = Math.max( 1, opts?.maxCols ?? computedCols )

	const isPresenting = !!opts?.isPresenting
	// const presenterRatio = Math.min( 0.95, Math.max( 0.5, opts?.presenterRatio ?? 0.6 ) )

	// --- Dynamic tweak for many participants ---
	let presenterRows = Math.max( 1, Math.floor( opts?.presenterRows ?? 2 ) )
	if ( n > 40 ) {
		presenterRows = Math.max( 1, presenterRows - 2 )
	} else if ( n > 20 ) {
		presenterRows = Math.max( 1, presenterRows - 1 )
	}

	const names = Array.from( { length: n }, ( _, i ) => indexToName( i ) )

	// ---- Non-presenting ----
	if ( !isPresenting ) {
		const { grid } = generateFactorIndexGrid( n, maxCols )
		return buildGridResponse( grid, names )
	}

	// ---- Presenter mode ----
	// ---- Presenter mode ----
	const othersCount = Math.max( 0, n - 1 )

	// decide orientation
	const isSideBySide = width >= 900

	// --- ADJUST PRESENTER SIZE BASED ON USER COUNT ---
	let presenterCols = Math.max( 2, Math.floor( maxCols * 0.6 ) )
	let adjustedPresenterRows = Math.max( 2, Math.floor( presenterRows * 0.9 ) )

	if ( n > 40 ) {
		// Big crowd → shrink more but keep visibly larger
		presenterCols = Math.max( 2, Math.floor( maxCols * 0.45 ) )
		adjustedPresenterRows = Math.max( 4, Math.floor( presenterRows * 0.7 ) )
	} else if ( n > 20 ) {
		// Medium crowd → slight shrink
		presenterCols = Math.max( 2, Math.floor( maxCols * 0.5 ) )
		adjustedPresenterRows = Math.max( 5, Math.floor( presenterRows * 0.8 ) )
	}

	if ( isSideBySide ) {
		// ----- Side-by-side -----
		const otherCols = Math.max( 0, maxCols - presenterCols )
		const sub = generateFactorIndexGrid( othersCount, otherCols )
		const subGridGlobal = sub.grid.map( ( row ) => row.map( ( cell ) => ( cell === -1 ? -1 : cell + 1 ) ) )
		const finalRows = Math.max( adjustedPresenterRows, sub.rows )
		const finalGrid = Array.from( { length: finalRows }, () => Array( maxCols ).fill( -1 ) )

		// Presenter fills left block
		for ( let r = 0; r < adjustedPresenterRows; r++ ) {
			for ( let c = 0; c < presenterCols; c++ ) finalGrid[r][c] = 0
		}

		// Others on right
		for ( let r = 0; r < sub.rows; r++ ) {
			const padLeft = Math.floor( ( otherCols - sub.cols ) / 2 )
			for ( let c = 0; c < otherCols; c++ ) {
				const subC = c - padLeft
				const globalCol = presenterCols + c
				if ( subC >= 0 && subC < sub.cols ) {
					finalGrid[r][globalCol] = subGridGlobal[r][subC]
				}
			}
		}

		// --- FIX: if only one unique "other" user, stretch across ALL rows ---
		const rightUsers = new Set( finalGrid.flat().filter( ( v ) => v > 0 ) )
		if ( rightUsers.size === 1 ) {
			const onlyUser = [...rightUsers][0]
			for ( let r = 0; r < finalRows; r++ ) {
				for ( let c = presenterCols; c < maxCols; c++ ) {
					finalGrid[r][c] = onlyUser
				}
			}
		}

		// Stretch dangling rows (multi-user case)
		for ( let r = 0; r < finalRows; r++ ) {
			const row = finalGrid[r]
			const filled = row.filter( ( v ) => v !== -1 )
			if ( filled.length > 0 && filled.length < maxCols ) {
				const stretchRow = []
				const stretchFactor = Math.floor( maxCols / filled.length )
				const extras = maxCols % filled.length
				let k = 0
				for ( let cell of filled ) {
					const repeatCount = stretchFactor + ( k < extras ? 1 : 0 )
					for ( let i = 0; i < repeatCount; i++ ) stretchRow.push( cell )
					k++
				}
				finalGrid[r] = stretchRow
			}
		}

		return buildGridResponse( finalGrid, names )
	}

	// ----- Stacked (presenter top, others below) -----
	const subBelow = generateFactorIndexGrid( othersCount, maxCols )
	const finalRows = presenterRows + subBelow.rows
	const finalGrid = Array.from( { length: finalRows }, () => Array( maxCols ).fill( -1 ) )

	// Presenter block
	for ( let r = 0; r < presenterRows; r++ ) {
		for ( let c = 0; c < maxCols; c++ ) finalGrid[r][c] = 0
	}

	// Others below
	for ( let r = 0; r < subBelow.rows; r++ ) {
		for ( let c = 0; c < subBelow.cols; c++ ) {
			const v = subBelow.grid[r][c]
			finalGrid[presenterRows + r][c] = v === -1 ? -1 : v + 1
		}
	}

	// Stretch dangling rows
	for ( let r = presenterRows; r < finalRows; r++ ) {
		const row = finalGrid[r]
		const filled = row.filter( ( v ) => v !== -1 )
		if ( filled.length > 0 && filled.length < maxCols ) {
			const stretchRow = []
			const stretchFactor = Math.floor( maxCols / filled.length )
			const extras = maxCols % filled.length
			let k = 0
			for ( let cell of filled ) {
				const repeatCount = stretchFactor + ( k < extras ? 1 : 0 )
				for ( let i = 0; i < repeatCount; i++ ) stretchRow.push( cell )
				k++
			}
			finalGrid[r] = stretchRow
		}
	}

	return buildGridResponse( finalGrid, names )
}

export const getTransformedMeetingType = ( type, options = {} ) => {

	const { forDisplay = false, plural = false } = options

	let returnData = type || ""

	switch ( type ) {
		case "class":
			returnData = forDisplay ? `online class${plural ? "es" : ""}` : `class`
			break
		case "hybrid-class":
			returnData = forDisplay ? `hybrid class${plural ? "es" : ""}` : `hybrid-class${plural ? "es" : ""}`
			break
		case "webinar":
			returnData = `webinar${plural ? "s" : ""}`
			break
		case "discussion":
			returnData = `meeting${plural ? "s" : ""}`
			break
		default:
			returnData = `meeting${plural ? "s" : ""}`
			break
	}

	if ( forDisplay )
		return returnData.replace( /-/g, " " )
	return returnData
}